import { getPageTitle } from './title.js';

function trimText(text, length) {
	if( text.length > length ){
		return text.substr(0, length) + '...';
	}

	return text;
}

function stripUrlState(path) {
	return path.split('#')[0].split('?')[0];
}

function addTrailingSlash(path) {
	if( path === '' ){
		return '/';
	}

	if( path.charAt(path.length - 1) === '/' ){
		return path;
	}

	return path + '/';
}

function getCanonicalPath(path) {
	return addTrailingSlash(stripUrlState(path));
}

function absoluteUrl(baseUrl, path) {
	if( path.indexOf('http://') === 0 || path.indexOf('https://') === 0 ){
		return path;
	}

	const cleanBaseUrl = baseUrl.replace(/\/$/, '');
	const cleanPath = path.charAt(0) === '/' ? path : '/' + path;

	return cleanBaseUrl + cleanPath;
}

function getLanguage(locale) {
	if( locale === 'ja' ){
		return 'ja-JP';
	}

	return 'en-US';
}

function getSchemaDate(date, year) {
	if( date ){
		const separated = date.split('-');

		return separated.map((value, index) => {
			if( index === 0 ){
				return value;
			}

			return value.padStart(2, '0');
		}).join('-');
	}

	if( year ){
		return String(year) + '-01-01';
	}

	return null;
}

function getSchemaDateTime(date, year) {
	const schemaDate = getSchemaDate(date, year);

	if( schemaDate ){
		return schemaDate + 'T00:00:00+09:00';
	}

	return null;
}

function getProjectGenre() {
	return [
		'Web Design',
		'Web Development',
		'Portfolio Website',
		'Interactive Website'
	];
}

function getOrganization(globalUrl, cdnUrl, includeCareersContact) {
	const organization = {
		'@type': 'Organization',
		'@id': absoluteUrl(globalUrl, '/#organization'),
		name: 'Garden Eight',
		description: 'Garden Eight is a digital design studio based in Tokyo.',
		url: absoluteUrl(globalUrl, '/'),
		email: 'hello@garden-eight.com',
		address: {
			'@type': 'PostalAddress',
			streetAddress: 'Kitazawa Takara Bld. 4F, 3-20-18 Kitazawa',
			addressLocality: 'Setagaya-ku',
			addressRegion: 'Tokyo',
			postalCode: '155-0031',
			addressCountry: 'JP'
		},
		contactPoint: [
			{
				'@type': 'ContactPoint',
				contactType: 'project inquiries',
				email: 'hello@garden-eight.com',
				availableLanguage: [
					'en',
					'ja'
				]
			},
		],
		logo: absoluteUrl(cdnUrl, '/assets/icon/icon-192x192.png'),
		image: absoluteUrl(cdnUrl, '/assets/img/ogp.png'),
		sameAs: [
			'https://www.instagram.com/garden_eight/',
			'https://twitter.com/GardenEight/',
			'https://www.linkedin.com/company/gardeneight/'
		],
		knowsAbout: [
			"Digital Design",
			"Web Design",
			"Web Development",
			"Front-end Development",
			"Interactive Design",
			"Creative Coding",
			"WebGL",
			"GLSL",
			"3D Web Experiences",
			"Motion Design",
			"UI Animation",
			"Digital Branding"
		]
	};

	if( includeCareersContact ){
		organization.contactPoint.push({
			'@type': 'ContactPoint',
			contactType: 'careers',
			email: 'join@garden-eight.com',
			availableLanguage: [
				'en',
				'ja'
			]
		});
	}

	return organization;
}

function getWebSite(globalUrl) {
	return {
		'@type': 'WebSite',
		'@id': absoluteUrl(globalUrl, '/#website'),
		name: 'Garden Eight',
		url: absoluteUrl(globalUrl, '/'),
		inLanguage: [
			'en-US',
			'ja-JP'
		],
		publisher: {
			'@id': absoluteUrl(globalUrl, '/#organization')
		}
	};
}

function getBreadcrumb(params) {
	const itemListElement = [
		{
			'@type': 'ListItem',
			position: 1,
			name: 'Home',
			item: absoluteUrl(params.globalUrl, params.locale === 'ja' ? '/ja/' : '/')
		}
	];

	if( params.project ){
		itemListElement.push(
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Cases',
				item: absoluteUrl(params.globalUrl, params.locale === 'ja' ? '/ja/cases/' : '/cases/')
			},
			{
				'@type': 'ListItem',
				position: 3,
				name: params.project.title,
				item: params.url
			}
		);
	} else if( params.title !== getPageTitle(params.locale) ){
		itemListElement.push({
			'@type': 'ListItem',
			position: 2,
			name: params.title.replace(' | ' + getPageTitle(params.locale), ''),
			item: params.url
		});
	}

	return {
		'@type': 'BreadcrumbList',
		'@id': params.url + '#breadcrumb',
		itemListElement: itemListElement
	};
}

function getWebPage(params) {
	const primaryImageUrl = params.project && params.project.poster ? absoluteUrl(params.cdnUrl, params.project.poster) : absoluteUrl(params.cdnUrl, '/assets/img/ogp.png');
	const page = {
		'@type': params.type,
		'@id': params.url + '#webpage',
		url: params.url,
		name: params.title,
		description: params.description,
		inLanguage: getLanguage(params.locale),
		isPartOf: {
			'@id': absoluteUrl(params.globalUrl, '/#website')
		},
		about: {
			'@id': absoluteUrl(params.globalUrl, '/#organization')
		},
		primaryImageOfPage: {
			'@type': 'ImageObject',
			url: primaryImageUrl
		}
	};

	if( params.mainEntity ){
		page.mainEntity = params.mainEntity;
	}

	return page;
}

function getVideoObject(params) {
	const project = params.project;
	const videoObject = {
		'@type': 'VideoObject',
		'@id': params.url + '#video',
		name: project.title + ' project video',
		description: project.description,
		thumbnailUrl: absoluteUrl(params.cdnUrl, project.videoObject.poster),
		contentUrl: absoluteUrl(params.cdnUrl, project.videoObject.src),
		uploadDate: getSchemaDateTime(project.publishDate, project.year)
	};
	const width = project.videoObject.width || project.videoObject.w;
	const height = project.videoObject.height || project.videoObject.h;

	if( width ){
		videoObject.width = Number(width);
	}

	if( height ){
		videoObject.height = Number(height);
	}

	if( project.videoObject.duration ){
		videoObject.duration = project.videoObject.duration;
	}

	return videoObject;
}

function getProjectEntity(params) {
	const project = params.project;
	const entity = {
		'@type': 'CreativeWork',
		'@id': params.url + '#creativework',
		name: project.title,
		description: project.description,
		url: params.url,
		inLanguage: getLanguage(params.locale),
		isPartOf: {
			'@id': absoluteUrl(params.globalUrl, '/#website')
		},
		creator: {
			'@id': absoluteUrl(params.globalUrl, '/#organization')
		}
	};
	const datePublished = getSchemaDate(project.publishDate, project.year);

	if( datePublished ){
		entity.datePublished = datePublished;
	}

	if( project.category && project.category.length ){
		entity.genre = getProjectGenre();
	}

	if( project.poster ){
		entity.image = absoluteUrl(params.cdnUrl, project.poster);
	}

	if( project.videoObject ){
		entity.video = {
			'@id': params.url + '#video'
		};
	}

	return entity;
}

function getProjectsItemList(params) {
	const itemListElement = [];

	params.projects.forEach((project) => {
		if( project.use ){
			itemListElement.push({
				'@type': 'ListItem',
				position: itemListElement.length + 1,
				url: absoluteUrl(params.globalUrl, params.locale === 'ja' ? '/ja/cases/' + project.slug + '/' : '/cases/' + project.slug + '/'),
				name: project.title
			});
		}
	});

	return {
		'@type': 'ItemList',
		'@id': params.url + '#itemlist',
		itemListElement: itemListElement
	};
}

function buildJsonLdScript(graph) {
	return {
		hid: 'base-jsonld',
		type: 'application/ld+json',
		json: {
			'@context': 'https://schema.org',
			'@graph': graph
		}
	};
}

function buildBaseJsonLd(params) {
	const url = absoluteUrl(params.globalUrl, getCanonicalPath(params.path));
	const assetBaseUrl = params.cdnUrl || params.globalUrl;
	const graph = [
		getOrganization(params.globalUrl, assetBaseUrl, params.type === 'ContactPage'),
		getWebSite(params.globalUrl)
	];
	const pageParams = {
		type: params.type,
		title: params.title,
		description: trimText(params.description, 160),
		url: url,
		locale: params.locale,
		globalUrl: params.globalUrl,
		cdnUrl: assetBaseUrl
	};
	const breadcrumb = getBreadcrumb({
		title: params.title,
		url: url,
		locale: params.locale,
		globalUrl: params.globalUrl,
		project: params.project
	});

	graph.push(breadcrumb);

	if( params.project ){
		const creativeWork = getProjectEntity({
			project: params.project,
			url: url,
			globalUrl: params.globalUrl,
			cdnUrl: assetBaseUrl,
			locale: params.locale
		});

		pageParams.type = 'WebPage';
		pageParams.project = params.project;
		pageParams.mainEntity = {
			'@id': creativeWork['@id']
		};

		graph.push(getWebPage(pageParams));
		graph.push(creativeWork);
		if( params.project.videoObject ){
			graph.push(getVideoObject({
				project: params.project,
				url: url,
				cdnUrl: assetBaseUrl
			}));
		}

		return buildJsonLdScript(graph);
	}

	if( params.type === 'FAQPage' ){
		return buildJsonLdScript(graph);
	}

	if( params.projects ){
		pageParams.mainEntity = getProjectsItemList({
			projects: params.projects,
			url: url,
			locale: params.locale,
			globalUrl: params.globalUrl
		});
		graph.push(pageParams.mainEntity);
	}

	graph.push(getWebPage(pageParams));

	return buildJsonLdScript(graph);
}

export { buildBaseJsonLd };
